import { forwardRef, useId, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <ProgressBar>. Value
// clamping, ARIA wiring (indeterminate vs determinate), and CSS ported
// letter-for-letter from the vendor's compiled bundle and its ProgressBar
// CSS — new .rc-progressbar class names. The fill still reads
// `var(--ds-color-brand-600)` (unrenamed — it's a real design token, not
// a component-scoped one) specifically because 3 real call sites in
// reports/page.tsx override it via an inline `style` custom property to
// recolor the bar per-instance, bypassing `variant` — renaming it would
// have silently broken that escape hatch.
export type ProgressBarSize = "sm" | "md" | "lg";
export type ProgressBarVariant = "default" | "success" | "warning" | "danger";

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  value?: number | null;
  min?: number;
  max?: number;
  label?: ReactNode;
  "aria-label"?: string;
  valueText?: ReactNode;
  size?: ProgressBarSize;
  variant?: ProgressBarVariant;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(Math.max(n, lo), hi);
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { value, min = 0, max = 100, label, valueText, size = "md", variant = "default", className, id: providedId, "aria-label": ariaLabel, ...rest },
  ref
) {
  const fallbackId = useId();
  const id = providedId ?? fallbackId;
  const labelId = label ? `${id}-label` : undefined;
  const indeterminate = value === undefined || value === null;
  const clamped = indeterminate ? min : clamp(value, min, max);
  const pct = indeterminate ? 0 : ((clamped - min) / (max - min)) * 100;

  const ariaProps: Record<string, unknown> = {
    role: "progressbar",
    "aria-valuemin": min,
    "aria-valuemax": max,
  };
  if (!indeterminate) {
    ariaProps["aria-valuenow"] = clamped;
    if (typeof valueText === "string") ariaProps["aria-valuetext"] = valueText;
  }
  if (labelId) ariaProps["aria-labelledby"] = labelId;
  else if (ariaLabel) ariaProps["aria-label"] = ariaLabel;

  return (
    <div
      ref={ref}
      data-size={size}
      data-variant={variant}
      data-indeterminate={indeterminate}
      className={cn("rc-progressbar", className)}
      {...rest}
    >
      {label || valueText ? (
        <div className="rc-progressbar__header">
          {label ? (
            <span id={labelId} className="rc-progressbar__label">
              {label}
            </span>
          ) : null}
          {valueText ? <span className="rc-progressbar__value">{valueText}</span> : null}
        </div>
      ) : null}
      <div className="rc-progressbar__track" {...ariaProps}>
        <div className="rc-progressbar__fill" style={{ width: `${pct}%` } as CSSProperties} />
      </div>
    </div>
  );
});
